import TronHeader from '@/components/tronscan/TronHeader';
import SearchBar from '@/components/tronscan/SearchBar';
import NetworkStatsGrid from '@/components/tronscan/NetworkStatsGrid';
import TokenPriceCard from '@/components/tronscan/TokenPriceCard';
import BlocksList from '@/components/tronscan/BlocksList';
import TransactionsList from '@/components/tronscan/TransactionsList';
import TransactionChart from '@/components/tronscan/TransactionChart';
import LiveIndicator from '@/components/tronscan/LiveIndicator';
import { useTronSocketContext } from '@/contexts/TronSocketContext';
import { useTranslation } from 'react-i18next';

import { ChevronRight, Sparkles, Zap, Shield, Globe } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useStakingConfig } from '@/hooks/useStakingConfig';

const Index = () => {
  const { data, isConnected } = useTronSocketContext();
  const { t } = useTranslation();
  const { effectiveApy } = useStakingConfig();

  return (
    <div className="min-h-screen bg-background">
      <TronHeader />

      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-32">
        {/* Hero Section - Compact Glass Style */}
        <div className="relative mb-4 sm:mb-6">
          {/* Subtle background glow */}
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[200px] bg-gradient-to-r from-primary/10 via-cyan-500/10 to-primary/10 rounded-full blur-3xl opacity-60" />
          </div>
          
          <div className="text-center py-3 sm:py-4">
            {/* Main heading */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
              <Sparkles className="w-5 h-5 sm:w-7 sm:h-7 text-primary drop-shadow-[0_0_15px_hsl(var(--primary)/0.8)] animate-pulse" />
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold">
                <span className="bg-gradient-to-r from-primary via-cyan-500 to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                  TRON
                </span>
                <span className="text-foreground"> {t('hero.titleSuffix', 'Blockchain Explorer')}</span>
              </h1>
            </div>
            
            {/* Subtitle */}
            <p className="text-xs sm:text-sm text-muted-foreground mb-2 max-w-lg mx-auto">
              {t('hero.description', 'Explore blocks, transactions, accounts and smart contracts on the TRON network')}
            </p>
            
            {/* Online indicator */}
            <div className="flex justify-center mb-2">
              <LiveIndicator isConnected={isConnected} lastUpdate={data.lastUpdate} />
            </div>
          </div>
        </div>

        {/* Search - Glass Style */}
        <div className="mb-4 sm:mb-6">
          <SearchBar />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="lg:col-span-2">
            <NetworkStatsGrid socketData={data} />
          </div>
          <div>
            <TokenPriceCard socketData={data} />
          </div>
        </div>

        {/* Quick Actions - Unified Glass Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Staking Promo */}
          <div className="group relative p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 backdrop-blur-xl border border-border/50 hover:border-primary/40 transition-all duration-300 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="font-semibold text-foreground text-sm sm:text-base">{t('home.staking.title', 'Earn Rewards')}</p>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">{t('home.staking.description', `Stake TRX or USDT and earn up to ${effectiveApy.usdt}% APY.`)}</p>
              <div className="flex items-center gap-3 mb-3">
                <div className="px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-lg sm:text-xl font-bold text-primary">{effectiveApy.usdt}%</p>
                  <p className="text-[10px] text-muted-foreground">USDT APY</p>
                </div>
                <div className="px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-lg sm:text-xl font-bold text-green-500">{effectiveApy.trx}%</p>
                  <p className="text-[10px] text-muted-foreground">TRX APY</p>
                </div>
              </div>
              <a href="/staking" className="inline-flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline font-medium group-hover:gap-2 transition-all">
                {t('home.staking.cta', 'Start Staking')}
                <ChevronRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Developer API */}
          <div className="group relative p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 backdrop-blur-xl border border-border/50 hover:border-chart-2/40 transition-all duration-300 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-chart-2/30 to-transparent" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-chart-2/15 flex items-center justify-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-chart-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <p className="font-semibold text-foreground text-sm sm:text-base">{t('home.api.title', 'Developer API')}</p>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">{t('home.api.description', 'Access TRON blockchain data with our powerful REST API.')}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="px-2 py-1 bg-chart-2/10 text-chart-2 text-[10px] sm:text-xs rounded-lg font-mono border border-chart-2/20">accounts</span>
                <span className="px-2 py-1 bg-chart-2/10 text-chart-2 text-[10px] sm:text-xs rounded-lg font-mono border border-chart-2/20">transactions</span>
                <span className="px-2 py-1 bg-chart-2/10 text-chart-2 text-[10px] sm:text-xs rounded-lg font-mono border border-chart-2/20">blocks</span>
              </div>
              <a href="/api-docs" className="inline-flex items-center gap-1 text-xs sm:text-sm text-chart-2 hover:underline font-medium group-hover:gap-2 transition-all">
                {t('home.api.cta', 'View Documentation')}
                <ChevronRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Become Validator */}
          <div className="group relative p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 backdrop-blur-xl border border-border/50 hover:border-chart-4/40 transition-all duration-300 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-chart-4/30 to-transparent" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-chart-4/15 flex items-center justify-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-chart-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <p className="font-semibold text-foreground text-sm sm:text-base">{t('home.validator.title', 'Become Validator')}</p>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">{t('home.validator.description', 'Join TRON network as Super Representative.')}</p>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex -space-x-1.5">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-chart-4/30 to-chart-4/10 border-2 border-background flex items-center justify-center">
                      <span className="text-[9px] sm:text-xs font-bold text-chart-4">#{i}</span>
                    </div>
                  ))}
                </div>
                <span className="text-[10px] sm:text-xs text-muted-foreground">+27 SR</span>
              </div>
              <a href="/validators" className="inline-flex items-center gap-1 text-xs sm:text-sm text-chart-4 hover:underline font-medium group-hover:gap-2 transition-all">
                {t('home.validator.cta', 'View Validators')}
                <ChevronRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Blocks List */}
        <div className="mb-4 sm:mb-6">
          <BlocksList socketData={data} />
        </div>

        {/* Transactions and Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          <TransactionsList socketData={data} />
          <TransactionChart />
        </div>
      </main>

      {/* Fixed Bottom Glass Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2">
        <div className="relative p-3 sm:p-4 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 backdrop-blur-xl border border-border/50 shadow-2xl overflow-hidden max-w-2xl mx-auto">
          {/* Glass shine effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          
          <div className="relative flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Brand */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
                <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary drop-shadow-[0_0_8px_hsl(var(--primary)/0.6)]" />
              </div>
              <span className="font-semibold text-sm sm:text-base">Tron<span className="text-primary">scope</span></span>
              <span className="text-[10px] text-muted-foreground">© 2025</span>
            </div>
            
            {/* Links */}
            <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
              <a href="/terms" className="text-muted-foreground hover:text-foreground transition-colors">{t('footer.terms', 'Terms')}</a>
              <a href="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">{t('footer.privacy', 'Privacy')}</a>
              <a href="/contact" className="text-muted-foreground hover:text-foreground transition-colors">{t('nav.contact', 'Contact')}</a>
            </div>
            
            {/* Language */}
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
